import { type ReactNode, useCallback, useState } from 'react';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { msalInstance } from './msalInstance';
import { useIdleTimer } from '@/hooks/useIdleTimer';
import { SessionWarning } from '@components/common/SessionWarning';
import { loginRequest } from './msalConfig';
import { telemetry } from '@/services';
import { EVENT_TYPES } from '@/constants';

const IDLE_TIMEOUT = 60 * 60 * 1000;
const WARNING_TIME = 5 * 60 * 1000;

interface AuthProviderProps {
  readonly children: ReactNode;
}

function SessionManager({ children }: AuthProviderProps): ReactNode {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [showWarning, setShowWarning] = useState(false);

  const handleIdle = useCallback(() => {
    if (isAuthenticated) {
      telemetry.trackEvent('Session', EVENT_TYPES.SESSION_IDLE_REACHED, {
        reason: 'idle_threshold_crossed',
      });
    }
  }, [isAuthenticated]);

  const handleWarning = useCallback(() => {
    telemetry.trackEvent('Session', EVENT_TYPES.SESSION_WARNING);
    setShowWarning(true);
  }, []);

  const { reset } = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    warningTime: WARNING_TIME,
    onIdle: handleIdle,
    onWarning: handleWarning,
    enabled: isAuthenticated,
  });

  const handleExtendSession = useCallback(() => {
    telemetry.trackButtonClick('SessionWarning', 'ExtendSession');
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (account) {
      void instance
        .acquireTokenSilent({
          ...loginRequest,
          account,
          forceRefresh: true,
        })
        .catch(() => {});
    }
    reset();
    setShowWarning(false);
  }, [instance, reset]);

  const handleSignOut = useCallback(() => {
    telemetry.trackButtonClick('SessionWarning', 'SignOut');
    void instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  const handleDismissOutsideClick = useCallback(() => {
    const account = instance.getActiveAccount();
    if (account) {
      instance
        .acquireTokenSilent({ ...loginRequest, account })
        .then(() => {
          telemetry.trackEvent('Session', EVENT_TYPES.SESSION_DISMISSED_OUTSIDE_CLICK, {
            refresh: 'success',
          });
        })
        .catch(() => {
          telemetry.trackEvent('Session', EVENT_TYPES.SESSION_DISMISSED_OUTSIDE_CLICK, {
            refresh: 'failure',
          });
        });
    } else {
      telemetry.trackEvent('Session', EVENT_TYPES.SESSION_DISMISSED_OUTSIDE_CLICK, {
        refresh: 'no_account',
      });
    }
    reset();
    setShowWarning(false);
  }, [instance, reset]);

  const handleDismissEscape = useCallback(() => {
    const account = instance.getActiveAccount();
    if (account) {
      instance
        .acquireTokenSilent({ ...loginRequest, account })
        .then(() => {
          telemetry.trackEvent('Session', EVENT_TYPES.SESSION_DISMISSED_ESCAPE, {
            refresh: 'success',
          });
        })
        .catch(() => {
          telemetry.trackEvent('Session', EVENT_TYPES.SESSION_DISMISSED_ESCAPE, {
            refresh: 'failure',
          });
        });
    } else {
      telemetry.trackEvent('Session', EVENT_TYPES.SESSION_DISMISSED_ESCAPE, {
        refresh: 'no_account',
      });
    }
    reset();
    setShowWarning(false);
  }, [instance, reset]);

  return (
    <>
      {children}
      <SessionWarning
        open={showWarning}
        onExtend={handleExtendSession}
        onSignOut={handleSignOut}
        onDismissOutside={handleDismissOutsideClick}
        onDismissEscape={handleDismissEscape}
      />
    </>
  );
}

export function AuthProvider({ children }: AuthProviderProps): ReactNode {
  return (
    <MsalProvider instance={msalInstance}>
      <SessionManager>{children}</SessionManager>
    </MsalProvider>
  );
}
